import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    ArrowRightCircle,
    Ban,
    CheckCircle2,
    Cog,
    Download,
    ExternalLink,
    FileText,
    History as HistoryIcon,
    Link as LinkIcon,
    Loader2,
    Plus,
    Save,
    Send,
    Trash2,
    Unlink,
    XCircle,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { ProposalStatusBadge } from "@/components/common/ProposalStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

import { proposalsService } from "@/services/proposals.service";
import { workOrdersService } from "@/services/work_orders.service";
import { getErrorMessage } from "@/services/api";
import { formatBRL } from "@/config/constants";
import { formatDateTime, mediaUrl } from "@/lib/utils";
import {
    Select as UISelect,
    SelectContent as UISelectContent,
    SelectItem as UISelectItem,
    SelectTrigger as UISelectTrigger,
    SelectValue as UISelectValue,
} from "@/components/ui/select";

function InfoBlock({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="text-sm text-foreground">{value ?? "—"}</div>
        </div>
    );
}

function ScopeBlock({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap">{value}</div>
        </div>
    );
}

// -------------------------------------------------------------------------
// Services / Parts editors
// -------------------------------------------------------------------------
function ServiceRow({ service, disabled, onUpdate, onRemove }) {
    const [description, setDescription] = useState(service.description);
    const [quantity, setQuantity] = useState(service.quantity);
    const [unitPrice, setUnitPrice] = useState(service.unit_price);
    useEffect(() => {
        setDescription(service.description);
        setQuantity(service.quantity);
        setUnitPrice(service.unit_price);
    }, [service.id, service.description, service.quantity, service.unit_price]);

    const total = Number(quantity || 0) * Number(unitPrice || 0);
    const dirty = description !== service.description
        || Number(quantity) !== Number(service.quantity)
        || Number(unitPrice) !== Number(service.unit_price);

    return (
        <tr className="border-b last:border-0" data-testid={`service-row-${service.id}`}>
            <td className="py-2 pr-2">
                <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={disabled} className="h-8" />
            </td>
            <td className="py-2 pr-2 w-24">
                <Input type="number" step="0.5" value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={disabled} className="h-8 text-right" />
            </td>
            <td className="py-2 pr-2 w-32">
                <Input type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} disabled={disabled} className="h-8 text-right" />
            </td>
            <td className="py-2 pr-2 w-32 text-right font-mono text-sm">
                {formatBRL(total)}
            </td>
            <td className="py-2 w-24 text-right">
                {dirty && !disabled && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-secondary"
                        onClick={() => onUpdate({
                            description,
                            quantity: Number(quantity),
                            unit_price: Number(unitPrice),
                        })}
                        data-testid={`service-save-${service.id}`}
                    >
                        <Save className="h-4 w-4" />
                    </Button>
                )}
                {!disabled && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-danger"
                        onClick={onRemove}
                        data-testid={`service-remove-${service.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </td>
        </tr>
    );
}

function PartRow({ part, disabled, onUpdate, onRemove }) {
    const [description, setDescription] = useState(part.description);
    const [quantity, setQuantity] = useState(part.quantity);
    const [unitPrice, setUnitPrice] = useState(part.unit_price);
    const [obs, setObs] = useState(part.observation || "");
    useEffect(() => {
        setDescription(part.description);
        setQuantity(part.quantity);
        setUnitPrice(part.unit_price);
        setObs(part.observation || "");
    }, [part.id, part.description, part.quantity, part.unit_price, part.observation]);

    const total = Number(quantity || 0) * Number(unitPrice || 0);
    const dirty = description !== part.description
        || Number(quantity) !== Number(part.quantity)
        || Number(unitPrice) !== Number(part.unit_price)
        || obs !== (part.observation || "");

    return (
        <tr className="border-b last:border-0" data-testid={`part-row-${part.id}`}>
            <td className="py-2 pr-2">
                <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={disabled} className="h-8" />
                <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação" disabled={disabled} className="mt-1 h-7 text-xs" />
            </td>
            <td className="py-2 pr-2 w-24">
                <Input type="number" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={disabled} className="h-8 text-right" />
            </td>
            <td className="py-2 pr-2 w-32">
                <Input type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} disabled={disabled} className="h-8 text-right" />
            </td>
            <td className="py-2 pr-2 w-32 text-right font-mono text-sm">
                {formatBRL(total)}
            </td>
            <td className="py-2 w-24 text-right">
                {dirty && !disabled && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-secondary"
                        onClick={() => onUpdate({
                            description,
                            quantity: Number(quantity),
                            unit_price: Number(unitPrice),
                            observation: obs || null,
                        })}
                        data-testid={`part-save-${part.id}`}
                    >
                        <Save className="h-4 w-4" />
                    </Button>
                )}
                {!disabled && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-danger"
                        onClick={onRemove}
                        data-testid={`part-remove-${part.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </td>
        </tr>
    );
}

// -------------------------------------------------------------------------
// Main details page
// -------------------------------------------------------------------------
export default function ProposalDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const [rejectOpen, setRejectOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [linkWoOpen, setLinkWoOpen] = useState(false);
    const [newService, setNewService] = useState({ description: "", quantity: 1, unit_price: 0 });
    const [newPart, setNewPart] = useState({ description: "", quantity: 1, unit_price: 0 });

    const proposal = useQuery({
        queryKey: ["proposal", id],
        queryFn: () => proposalsService.get(id),
        enabled: !!id,
    });

    const p = proposal.data;
    const isEditable = p?.status === "rascunho" || p?.status === "aguardando_aprovacao";
    const isDraft = p?.status === "rascunho";

    // Memoize sorted history to avoid re-sorting on every render.
    const sortedHistory = useMemo(() => {
        const history = p?.history || [];
        return [...history].sort(
            (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
        );
    }, [p?.history]);

    // Header form (validity, delivery, discount, labor, commercial terms)
    const {
        register, handleSubmit, reset, watch,
        formState: { isDirty },
    } = useForm({
        defaultValues: {
            validity_days: 30,
            delivery_time_days: "",
            discount_amount: 0,
            labor_estimated_hours: 0,
            labor_hourly_rate: 0,
            payment_terms: "",
            warranty: "",
            delivery_time: "",
            observations: "",
        },
    });
    useEffect(() => {
        if (!p) return;
        reset({
            validity_days: p.validity_days ?? 30,
            delivery_time_days: p.delivery_time_days ?? "",
            discount_amount: p.discount_amount ?? 0,
            labor_estimated_hours: p.labor?.estimated_hours ?? 0,
            labor_hourly_rate: p.labor?.hourly_rate ?? 0,
            payment_terms: p.commercial_terms?.payment_terms || "",
            warranty: p.commercial_terms?.warranty || "",
            delivery_time: p.commercial_terms?.delivery_time || "",
            observations: p.commercial_terms?.observations || "",
        });
    }, [p, reset]);

    const patchHeader = useMutation({
        mutationFn: (data) => proposalsService.patch(id, {
            validity_days: data.validity_days !== "" ? Number(data.validity_days) : null,
            delivery_time_days: data.delivery_time_days !== "" ? Number(data.delivery_time_days) : null,
            discount_amount: Number(data.discount_amount || 0),
            labor: {
                estimated_hours: Number(data.labor_estimated_hours || 0),
                hourly_rate: Number(data.labor_hourly_rate || 0),
            },
            commercial_terms: {
                payment_terms: data.payment_terms || null,
                warranty: data.warranty || null,
                delivery_time: data.delivery_time || null,
                observations: data.observations || null,
            },
        }),
        onSuccess: () => {
            toast.success("Proposta atualizada.");
            qc.invalidateQueries({ queryKey: ["proposal", id] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar proposta.")),
    });

    const addService = useMutation({
        mutationFn: (payload) => proposalsService.addService(id, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            setNewService({ description: "", quantity: 1, unit_price: 0 });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao adicionar serviço.")),
    });
    const updateService = useMutation({
        mutationFn: ({ sid, payload }) => proposalsService.updateService(id, sid, payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["proposal", id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar serviço.")),
    });
    const removeService = useMutation({
        mutationFn: (sid) => proposalsService.removeService(id, sid),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["proposal", id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover serviço.")),
    });
    const addPart = useMutation({
        mutationFn: (payload) => proposalsService.addPart(id, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            setNewPart({ description: "", quantity: 1, unit_price: 0 });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao adicionar peça.")),
    });
    const updatePart = useMutation({
        mutationFn: ({ pid, payload }) => proposalsService.updatePart(id, pid, payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["proposal", id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar peça.")),
    });
    const removePart = useMutation({
        mutationFn: (pid) => proposalsService.removePart(id, pid),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["proposal", id] }),
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover peça.")),
    });
    const sendMutation = useMutation({
        mutationFn: () => proposalsService.send(id),
        onSuccess: () => {
            toast.success("Proposta enviada para aprovação.");
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            qc.invalidateQueries({ queryKey: ["proposals"] });
            qc.invalidateQueries({ queryKey: ["proposals-dashboard"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao enviar proposta.")),
    });
    const approveMutation = useMutation({
        mutationFn: (payload) => proposalsService.approve(id, payload),
        onSuccess: (updated) => {
            const woNum = updated?.work_order_number;
            toast.success(
                woNum
                    ? `Proposta aprovada. OS ${woNum} finalizada automaticamente.`
                    : "Proposta aprovada. Conta a receber gerada no Financeiro.",
            );
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            qc.invalidateQueries({ queryKey: ["proposals"] });
            qc.invalidateQueries({ queryKey: ["proposals-dashboard"] });
            qc.invalidateQueries({ queryKey: ["work_orders"] });
            setApproveOpen(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao aprovar proposta.")),
    });
    const rejectMutation = useMutation({
        mutationFn: (payload) => proposalsService.reject(id, payload),
        onSuccess: () => {
            toast.success("Proposta rejeitada.");
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            setRejectOpen(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao rejeitar proposta.")),
    });
    const cancelMutation = useMutation({
        mutationFn: () => proposalsService.cancel(id, { reason: "Cancelada pelo administrador." }),
        onSuccess: () => {
            toast.success("Proposta cancelada.");
            qc.invalidateQueries({ queryKey: ["proposal", id] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao cancelar proposta.")),
    });
    const pdfMutation = useMutation({
        mutationFn: () => proposalsService.generatePdf(id),
        onSuccess: (updated) => {
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            const url = updated?.pdf?.url;
            if (url) {
                toast.success("PDF gerado. Clique em \"Abrir PDF\" para visualizar.");
            } else {
                toast.success("PDF gerado.");
            }
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao gerar PDF.")),
    });
    // Background auto-heal: when the proposal is loaded, if it has a PDF URL
    // that might be stale (production runs on ephemeral Kubernetes storage
    // that wipes uploads on redeploy), silently refresh via the ensure
    // endpoint so the "Abrir PDF" anchor always points at a valid file.
    // We only trigger this once per proposal id and only when a URL exists.
    const pdfUrl = proposal.data?.pdf?.url;
    const [pdfEnsured, setPdfEnsured] = useState(false);
    useEffect(() => {
        setPdfEnsured(false);
    }, [id]);
    useEffect(() => {
        if (!pdfUrl || pdfEnsured) return;
        let cancelled = false;
        proposalsService.ensurePdf(id).then((updated) => {
            if (cancelled) return;
            setPdfEnsured(true);
            // Only refresh cache when the backend actually rebuilt the file
            // (i.e. the URL changed) to avoid re-renders on the fast path.
            if (updated?.pdf?.url && updated.pdf.url !== pdfUrl) {
                qc.invalidateQueries({ queryKey: ["proposal", id] });
            }
        }).catch(() => {
            // Silent: worst case the anchor will 404 and the user regenerates.
        });
        return () => { cancelled = true; };
    }, [id, pdfUrl, pdfEnsured, qc]);
    const linkWoMutation = useMutation({
        mutationFn: (workOrderId) => proposalsService.patch(id, { work_order_id: workOrderId }),
        onSuccess: () => {
            toast.success("Ordem de Serviço vinculada.");
            qc.invalidateQueries({ queryKey: ["proposal", id] });
            setLinkWoOpen(false);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao vincular OS.")),
    });

    if (proposal.isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-96" />
                <Skeleton className="h-32" />
                <Skeleton className="h-96" />
            </div>
        );
    }
    if (proposal.isError || !p) {
        return (
            <div className="rounded-md border border-dashed p-8 text-center">
                <div className="text-sm font-medium">Proposta não encontrada.</div>
                <Button variant="ghost" className="mt-3" onClick={() => navigate("/proposals")}>
                    Voltar para a lista
                </Button>
            </div>
        );
    }

    const customer = p.customer_snapshot || {};
    const asset = p.asset_snapshot || {};
    const scope = p.diagnosis_snapshot || {};
    const diag = scope.diagnosis || {};
    const report = scope.technical_report || {};
    const totals = p.totals || {};

    return (
        <div className="space-y-6" data-testid="proposal-details-page">
            <PageHeader
                title={`Proposta ${p.number}`}
                description={
                    p.work_order_number
                        ? `OS ${p.work_order_number} · Oficina · Emitida em ${
                            p.issue_date ? formatDateTime(p.issue_date) : "—"
                        }`
                        : `${p.title || "Proposta avulsa"} · Emitida em ${
                            p.issue_date ? formatDateTime(p.issue_date) : "—"
                        }`
                }
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Propostas", to: "/proposals" },
                    { label: p.number },
                ]}
                backTo="/proposals"
                actions={
                    <>
                        <ProposalStatusBadge value={p.status} size="lg" />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => pdfMutation.mutate()}
                            disabled={pdfMutation.isPending}
                            data-testid="proposal-pdf-button"
                        >
                            {pdfMutation.isPending
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Download className="mr-2 h-4 w-4" />}
                            {p.pdf?.url ? "Gerar PDF" : "Gerar PDF"}
                        </Button>
                        {p.pdf?.url && (
                            <a
                                href={mediaUrl(p.pdf.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-8 items-center rounded-md px-3 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                data-testid="proposal-pdf-open"
                            >
                                <FileText className="mr-2 h-4 w-4" />
                                Abrir PDF atual
                            </a>
                        )}
                        {isDraft && (
                            <Button
                                size="sm"
                                onClick={() => sendMutation.mutate()}
                                disabled={sendMutation.isPending}
                                className="bg-yellow-600 text-white hover:bg-yellow-700"
                                data-testid="proposal-send-button"
                            >
                                {sendMutation.isPending
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Send className="mr-2 h-4 w-4" />}
                                Enviar para aprovação
                            </Button>
                        )}
                        {(p.status === "aguardando_aprovacao" || p.status === "rascunho") && (
                            <>
                                <Button
                                    size="sm"
                                    onClick={() => setApproveOpen(true)}
                                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                                    data-testid="proposal-approve-button"
                                >
                                    <CheckCircle2 className="mr-2 h-4 w-4" /> Aprovar
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRejectOpen(true)}
                                    className="border-red-500/40 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                                    data-testid="proposal-reject-button"
                                >
                                    <XCircle className="mr-2 h-4 w-4" /> Rejeitar
                                </Button>
                            </>
                        )}
                        {isEditable && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => cancelMutation.mutate()}
                                disabled={cancelMutation.isPending}
                                data-testid="proposal-cancel-button"
                            >
                                <Ban className="mr-2 h-4 w-4" /> Cancelar
                            </Button>
                        )}
                    </>
                }
            />

            {/* Header cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Cliente</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 text-sm">
                        <div className="font-medium">{customer.legal_name || "—"}</div>
                        {customer.trade_name && <div className="text-xs text-muted-foreground">{customer.trade_name}</div>}
                        {customer.tax_id && <div className="text-xs text-muted-foreground">CNPJ: {customer.tax_id}</div>}
                        {customer.phone && <div className="text-xs text-muted-foreground">{customer.phone}</div>}
                        {customer.email && <div className="text-xs text-muted-foreground">{customer.email}</div>}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Equipamento</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-1 text-sm">
                        <div className="font-mono text-xs">{asset.internal_code || "—"}</div>
                        <div>{asset.manufacturer} {asset.model}</div>
                        <div className="text-xs text-muted-foreground">
                            {asset.category}
                            {asset.serial_number ? ` · SN ${asset.serial_number}` : ""}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">OS de referência</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        {p.work_order_id ? (
                            <>
                                <Link
                                    to={`/work-orders/${p.work_order_id}`}
                                    className="inline-flex items-center gap-1 font-mono text-sm text-secondary hover:underline"
                                    data-testid="proposal-open-wo"
                                >
                                    {p.work_order_number} <ExternalLink className="h-3 w-3" />
                                </Link>
                                {p.workshop_id && (
                                    <Link
                                        to={`/workshop/${p.workshop_id}`}
                                        className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                                        data-testid="proposal-open-workshop"
                                    >
                                        <Cog className="h-3 w-3" /> Ver oficina
                                    </Link>
                                )}
                                {isEditable && !p.workshop_id && (
                                    <button
                                        type="button"
                                        onClick={() => setLinkWoOpen(true)}
                                        className="mt-1 block text-xs text-muted-foreground underline-offset-2 hover:underline"
                                        data-testid="proposal-change-wo"
                                    >
                                        Alterar OS vinculada
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="space-y-2">
                                <div className="text-xs text-muted-foreground">
                                    Nenhuma OS vinculada.
                                </div>
                                {isEditable && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setLinkWoOpen(true)}
                                        data-testid="proposal-link-wo-button"
                                    >
                                        <LinkIcon className="mr-2 h-3.5 w-3.5" /> Vincular OS
                                    </Button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="items" className="w-full">
                <TabsList data-testid="proposal-tabs">
                    <TabsTrigger value="items" data-testid="tab-items">Serviços & Peças</TabsTrigger>
                    <TabsTrigger value="terms" data-testid="tab-terms">Condições</TabsTrigger>
                    <TabsTrigger value="scope" data-testid="tab-scope">Escopo Técnico</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">Histórico</TabsTrigger>
                </TabsList>

                <TabsContent value="items" className="space-y-4 pt-4">
                    {/* Services */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">Serviços</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[11px] uppercase text-muted-foreground">
                                        <tr className="border-b">
                                            <th className="py-2 text-left">Descrição</th>
                                            <th className="py-2 text-right">Qtd.</th>
                                            <th className="py-2 text-right">Preço Unit.</th>
                                            <th className="py-2 text-right">Total</th>
                                            <th className="py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(p.services || []).map((s) => (
                                            <ServiceRow
                                                key={s.id}
                                                service={s}
                                                disabled={!isEditable}
                                                onUpdate={(payload) => updateService.mutate({ sid: s.id, payload })}
                                                onRemove={() => removeService.mutate(s.id)}
                                            />
                                        ))}
                                        {(p.services || []).length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="py-4 text-center text-xs text-muted-foreground">
                                                    Nenhum serviço adicionado.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {isEditable && (
                                <div className="mt-3 grid gap-2 md:grid-cols-[1fr_100px_140px_auto]">
                                    <Input
                                        placeholder="Descrição do serviço"
                                        value={newService.description}
                                        onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                                        data-testid="new-service-description"
                                    />
                                    <Input
                                        type="number" step="0.5" min={0}
                                        placeholder="Qtd."
                                        value={newService.quantity}
                                        onChange={(e) => setNewService({ ...newService, quantity: e.target.value })}
                                        data-testid="new-service-quantity"
                                    />
                                    <Input
                                        type="number" step="0.01" min={0}
                                        placeholder="Preço unit. (R$)"
                                        value={newService.unit_price}
                                        onChange={(e) => setNewService({ ...newService, unit_price: e.target.value })}
                                        data-testid="new-service-unit-price"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (!newService.description.trim()) {
                                                toast.error("Informe a descrição do serviço.");
                                                return;
                                            }
                                            addService.mutate({
                                                description: newService.description.trim(),
                                                quantity: Number(newService.quantity || 1),
                                                unit_price: Number(newService.unit_price || 0),
                                            });
                                        }}
                                        disabled={addService.isPending}
                                        data-testid="add-service-button"
                                    >
                                        <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Parts */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">Peças</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[11px] uppercase text-muted-foreground">
                                        <tr className="border-b">
                                            <th className="py-2 text-left">Descrição / Observação</th>
                                            <th className="py-2 text-right">Qtd.</th>
                                            <th className="py-2 text-right">Preço Unit.</th>
                                            <th className="py-2 text-right">Total</th>
                                            <th className="py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(p.parts || []).map((pt) => (
                                            <PartRow
                                                key={pt.id}
                                                part={pt}
                                                disabled={!isEditable}
                                                onUpdate={(payload) => updatePart.mutate({ pid: pt.id, payload })}
                                                onRemove={() => removePart.mutate(pt.id)}
                                            />
                                        ))}
                                        {(p.parts || []).length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="py-4 text-center text-xs text-muted-foreground">
                                                    Nenhuma peça adicionada.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {isEditable && (
                                <div className="mt-3 grid gap-2 md:grid-cols-[1fr_100px_140px_auto]">
                                    <Input
                                        placeholder="Descrição da peça"
                                        value={newPart.description}
                                        onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                                        data-testid="new-part-description"
                                    />
                                    <Input
                                        type="number" step="1" min={0}
                                        placeholder="Qtd."
                                        value={newPart.quantity}
                                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                                        data-testid="new-part-quantity"
                                    />
                                    <Input
                                        type="number" step="0.01" min={0}
                                        placeholder="Preço unit. (R$)"
                                        value={newPart.unit_price}
                                        onChange={(e) => setNewPart({ ...newPart, unit_price: e.target.value })}
                                        data-testid="new-part-unit-price"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (!newPart.description.trim()) {
                                                toast.error("Informe a descrição da peça.");
                                                return;
                                            }
                                            addPart.mutate({
                                                description: newPart.description.trim(),
                                                quantity: Number(newPart.quantity || 1),
                                                unit_price: Number(newPart.unit_price || 0),
                                            });
                                        }}
                                        disabled={addPart.isPending}
                                        data-testid="add-part-button"
                                    >
                                        <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Labor + Totals */}
                    <form
                        onSubmit={handleSubmit(patchHeader.mutate)}
                        className="grid gap-4 md:grid-cols-[1fr_360px]"
                    >
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                                    Mão de obra e prazos
                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                                        Interno
                                    </span>
                                </CardTitle>
                                <p className="pt-1 text-xs text-muted-foreground">
                                    Estas informações são apenas para controle interno — não aparecem no PDF enviado ao cliente. O valor da mão de obra é embutido em &quot;Serviços&quot; no documento final.
                                </p>
                            </CardHeader>
                            <CardContent className="grid gap-3 md:grid-cols-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Horas estimadas</Label>
                                    <Input type="number" step="0.5" min={0} disabled={!isEditable} {...register("labor_estimated_hours")} data-testid="labor-hours" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Valor/hora (R$)</Label>
                                    <Input type="number" step="0.01" min={0} disabled={!isEditable} {...register("labor_hourly_rate")} data-testid="labor-rate" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Validade (dias)</Label>
                                    <Input type="number" step="1" min={0} disabled={!isEditable} {...register("validity_days")} data-testid="validity-days" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Prazo entrega (dias)</Label>
                                    <Input type="number" step="1" min={0} disabled={!isEditable} {...register("delivery_time_days")} data-testid="delivery-time-days" />
                                </div>
                                <div className="space-y-1.5 md:col-span-2">
                                    <Label className="text-xs">Desconto (R$)</Label>
                                    <Input type="number" step="0.01" min={0} disabled={!isEditable} {...register("discount_amount")} data-testid="discount" />
                                </div>
                                <div className="flex items-end justify-end md:col-span-2">
                                    <Button
                                        type="submit"
                                        size="sm"
                                        disabled={!isEditable || !isDirty || patchHeader.isPending}
                                        className="bg-secondary text-secondary-foreground"
                                        data-testid="header-save-button"
                                    >
                                        <Save className="mr-2 h-4 w-4" /> Salvar
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-muted/30" data-testid="totals-card">
                            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Resumo</CardTitle></CardHeader>
                            <CardContent className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Serviços</span><span className="font-mono">{formatBRL(totals.services_total)}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Peças</span><span className="font-mono">{formatBRL(totals.parts_total)}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Mão de obra</span><span className="font-mono">{formatBRL(totals.labor_total)}</span></div>
                                <div className="flex justify-between"><span className="text-danger">Desconto</span><span className="font-mono text-danger">-{formatBRL(totals.discount_amount)}</span></div>
                                <div className="flex justify-between border-t pt-2 text-base font-semibold">
                                    <span>Total geral</span>
                                    <span className="font-mono text-brand-navy dark:text-foreground" data-testid="grand-total">
                                        {formatBRL(totals.grand_total)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </form>
                </TabsContent>

                <TabsContent value="terms" className="pt-4">
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Condições comerciais</CardTitle></CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit(patchHeader.mutate)} className="grid gap-3 md:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Condições de pagamento</Label>
                                    <Textarea rows={2} disabled={!isEditable} {...register("payment_terms")} data-testid="terms-payment" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Garantia</Label>
                                    <Textarea rows={2} disabled={!isEditable} {...register("warranty")} data-testid="terms-warranty" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Prazo de entrega</Label>
                                    <Textarea rows={2} disabled={!isEditable} {...register("delivery_time")} data-testid="terms-delivery" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Observações</Label>
                                    <Textarea rows={2} disabled={!isEditable} {...register("observations")} data-testid="terms-observations" />
                                </div>
                                <div className="md:col-span-2 flex justify-end">
                                    <Button
                                        type="submit"
                                        size="sm"
                                        disabled={!isEditable || !isDirty || patchHeader.isPending}
                                        className="bg-secondary text-secondary-foreground"
                                        data-testid="terms-save-button"
                                    >
                                        <Save className="mr-2 h-4 w-4" /> Salvar condições
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="scope" className="pt-4">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">Escopo técnico (importado do diagnóstico)</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <ScopeBlock label="Tipo de falha" value={diag.failure_type} />
                            <ScopeBlock label="Causa da falha" value={diag.failure_cause} />
                            <div className="md:col-span-2">
                                <ScopeBlock label="Descrição técnica" value={diag.detailed_description} />
                            </div>
                            <ScopeBlock label="Condição do equipamento" value={diag.equipment_general_condition} />
                            <ScopeBlock label="Recomendação técnica" value={diag.technical_recommendation} />
                            <div className="md:col-span-2 border-t pt-3 mt-2">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Laudo técnico</div>
                            </div>
                            <ScopeBlock label="Problema encontrado" value={report.problem_found} />
                            <ScopeBlock label="Causa raiz" value={report.root_cause} />
                            <ScopeBlock label="Solução recomendada" value={report.recommended_solution} />
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recomendação final</div>
                                <Badge variant="outline">{report.repair_recommendation || "—"}</Badge>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history" className="pt-4">
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><HistoryIcon className="h-4 w-4" />Histórico</CardTitle></CardHeader>
                        <CardContent>
                            {(p.history || []).length === 0 ? (
                                <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">Sem eventos.</div>
                            ) : (
                                <ol className="relative border-l pl-6">
                                    {sortedHistory.map((ev, i) => (
                                        <li key={`${ev.at || "no-date"}-${ev.action || "action"}-${i}`} className="mb-4 last:mb-0">
                                            <div className="absolute -left-2 grid h-4 w-4 place-items-center rounded-full border bg-background">
                                                <ArrowRightCircle className="h-3 w-3 text-muted-foreground" />
                                            </div>
                                            <div className="text-sm font-medium">{ev.action}</div>
                                            {ev.note && <div className="text-xs text-muted-foreground">{ev.note}</div>}
                                            <div className="text-[11px] text-muted-foreground">{ev.at ? formatDateTime(ev.at) : ""}</div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Approve dialog */}
            <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
                <DialogContent data-testid="approve-dialog">
                    <DialogHeader>
                        <DialogTitle>Aprovar proposta {p.number}</DialogTitle>
                        <DialogDescription className="text-xs">
                            {p.work_order_number ? (
                                <>
                                    A OS <span className="font-mono">{p.work_order_number}</span> será automaticamente marcada como <b>Finalizada</b> e uma <b>conta a receber</b> será gerada no Financeiro.
                                </>
                            ) : (
                                <>Uma <b>conta a receber</b> será gerada automaticamente no Financeiro após a aprovação.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <ApproveForm onSubmit={approveMutation.mutate} isPending={approveMutation.isPending} />
                </DialogContent>
            </Dialog>

            {/* Reject dialog */}
            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Rejeitar proposta {p.number}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Informe o motivo da rejeição.
                            {p.work_order_number && <> A OS permanece em <b>Em Atendimento</b>.</>}
                        </DialogDescription>
                    </DialogHeader>
                    <RejectForm onSubmit={rejectMutation.mutate} isPending={rejectMutation.isPending} />
                </DialogContent>
            </Dialog>

            {/* Link Work Order dialog */}
            <Dialog open={linkWoOpen} onOpenChange={setLinkWoOpen}>
                <DialogContent data-testid="link-wo-dialog">
                    <DialogHeader>
                        <DialogTitle>Vincular Ordem de Serviço</DialogTitle>
                        <DialogDescription className="text-xs">
                            Escolha uma OS do cliente para vincular a esta proposta. Se aprovada, a OS será finalizada automaticamente.
                        </DialogDescription>
                    </DialogHeader>
                    <LinkWorkOrderForm
                        customerId={p.customer_id}
                        currentWoId={p.work_order_id}
                        onSubmit={(woId) => linkWoMutation.mutate(woId)}
                        onUnlink={() => linkWoMutation.mutate(null)}
                        isPending={linkWoMutation.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function LinkWorkOrderForm({ customerId, currentWoId, onSubmit, onUnlink, isPending }) {
    const [selected, setSelected] = useState(currentWoId || "");
    const woQuery = useQuery({
        queryKey: ["work-orders-by-customer", customerId],
        queryFn: () =>
            workOrdersService.list({
                pageSize: 100,
                customer_id: customerId,
                sortBy: "created_at",
                sortOrder: "desc",
            }),
        enabled: !!customerId,
    });
    const items = woQuery.data?.items || [];

    return (
        <div className="space-y-3">
            <div className="space-y-1.5">
                <Label className="text-xs">Ordem de Serviço</Label>
                <UISelect value={selected} onValueChange={setSelected}>
                    <UISelectTrigger data-testid="link-wo-select">
                        <UISelectValue placeholder={items.length ? "Selecione uma OS" : "Nenhuma OS encontrada para este cliente"} />
                    </UISelectTrigger>
                    <UISelectContent>
                        {items.map((wo) => (
                            <UISelectItem key={wo.id} value={wo.id}>
                                {wo.number} · {wo.title || "OS"}
                            </UISelectItem>
                        ))}
                    </UISelectContent>
                </UISelect>
                {!items.length && (
                    <div className="text-xs text-muted-foreground">
                        Cadastre uma OS para este cliente antes de vincular.
                    </div>
                )}
            </div>
            <DialogFooter className="gap-2">
                {currentWoId && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onUnlink}
                        disabled={isPending}
                        data-testid="link-wo-unlink"
                    >
                        <Unlink className="mr-2 h-4 w-4" /> Desvincular
                    </Button>
                )}
                <Button
                    type="button"
                    onClick={() => selected && onSubmit(selected)}
                    disabled={!selected || isPending || selected === currentWoId}
                    data-testid="link-wo-submit"
                >
                    {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LinkIcon className="mr-2 h-4 w-4" />}
                    Vincular
                </Button>
            </DialogFooter>
        </div>
    );
}

function ApproveForm({ onSubmit, isPending }) {
    const [approvedBy, setApprovedBy] = useState("");
    const [note, setNote] = useState("");
    const [installments, setInstallments] = useState("1");
    const [firstDueDate, setFirstDueDate] = useState(() =>
        new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
    );
    return (
        <div className="space-y-3">
            <div className="space-y-1.5">
                <Label className="text-xs">Aprovado por (nome do cliente)</Label>
                <Input value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)} data-testid="approve-by-input" />
            </div>
            <div className="space-y-1.5">
                <Label className="text-xs">Anotação (opcional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="approve-note-input" />
            </div>
            <div className="rounded-md border border-dashed border-border/70 bg-muted/30 p-3">
                <div className="mb-2 text-xs font-medium">Plano financeiro (conta a receber)</div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <Label className="text-[10px] uppercase text-muted-foreground">Parcelas</Label>
                        <Input
                            type="number" min="1" max="60"
                            value={installments}
                            onChange={(e) => setInstallments(e.target.value)}
                            data-testid="approve-installments-input"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[10px] uppercase text-muted-foreground">1º vencimento</Label>
                        <Input
                            type="date"
                            value={firstDueDate}
                            onChange={(e) => setFirstDueDate(e.target.value)}
                            data-testid="approve-first-due-input"
                        />
                    </div>
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground">
                    Parcelas iguais com intervalo padrão de 30 dias.
                </p>
            </div>
            <DialogFooter>
                <Button
                    type="button"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    disabled={isPending}
                    onClick={() => onSubmit({
                        approved_by_customer: approvedBy || null,
                        approval_note: note || null,
                        installments_count: Math.max(1, Math.min(60, Number(installments) || 1)),
                        first_due_date: firstDueDate || null,
                    })}
                    data-testid="approve-confirm-button"
                >
                    {isPending
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    Confirmar aprovação
                </Button>
            </DialogFooter>
        </div>
    );
}

function RejectForm({ onSubmit, isPending }) {
    const [reason, setReason] = useState("");
    return (
        <div className="space-y-3">
            <div className="space-y-1.5">
                <Label className="text-xs">Motivo</Label>
                <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
            </div>
            <DialogFooter>
                <Button
                    type="button"
                    variant="outline"
                    className="border-red-500/40 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                    disabled={isPending}
                    onClick={() => onSubmit({ reason: reason || null })}
                    data-testid="reject-confirm-button"
                >
                    {isPending
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <XCircle className="mr-2 h-4 w-4" />}
                    Confirmar rejeição
                </Button>
            </DialogFooter>
        </div>
    );
}
